import clsx from 'clsx';
import React, {
  ChangeEvent,
  ReactElement,
  ReactNode,
  useEffect,
  useState,
} from 'react';
import { Label } from '../Label';
import { ArrowDropDown, ArrowDropUp } from '@material-ui/icons';
import { createPortal } from 'react-dom';
import { usePopper } from 'react-popper';
import { SelectItem } from './SelectItem';

interface Props extends BaseProps {
  anchorEl?: Element | null;
  children: ReactNode;
  disabled?: boolean;
  label?: string;
  maxWidth?: number;
  onChange(event: ChangeEvent<HTMLInputElement>): void;
  value: string | number | boolean;
  variant?: 'default' | 'contained';
}

const Select = (props: Props) => {
  const {
    anchorEl,
    children,
    className,
    disabled = false,
    fullWidth = false,
    helperText = null,
    label = null,
    maxWidth,
    onChange,
    placeholder,
    required,
    type,
    value,
    variant = 'default',
    ...rest
  } = props;
  const formattedLabel = (label || '')
    .toLowerCase()
    .trim()
    .replaceAll(' ', '-');
  const [selectedValue, setSelectedValue] = useState<any>(value);
  const [selectedText, setSelectedText] = useState(null);
  const [menuRef, setMenuRef] = useState<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const width = fullWidth ? 'full' : 'unset';
  const [referenceElement, setReferenceElement] =
    useState<HTMLDivElement | null>(null);
  const { styles, attributes } = usePopper(referenceElement, menuRef, {
    placement: 'bottom-start',
    strategy: 'fixed',
  });
  const customStyles = {
    maxWidth: maxWidth,
  };
  const classes = useStyles();

  useEffect(() => {
    setSelectedText(
      (children as ReactElement[]).find(
        (option: ReactElement) => option.props.value === value
      )?.props.children
    );
  }, [value]);

  useEffect(() => {
    function handleClick(event: any) {
      if (!menuRef?.contains(event.target) && event.target.id !== 'select-root')
        setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [menuRef]);

  const handleSelection = (event: any) => {
    if (event.target.value !== undefined) setSelectedValue(event.target.value);
    if (event.target.value !== undefined)
      setSelectedText(event.target.innerText);
    setOpen(false);

    menuRef?.addEventListener('change', () => onChange(event));
    const e = new Event('change');
    menuRef?.dispatchEvent(e);
  };

  const renderHelperText = () => {
    if (!helperText) return;
    return (
      <p
        className={clsx(classes.helperText.default, classes.helperText['info'])}
      >
        {helperText}
      </p>
    );
  };

  const renderArrow = () => {
    const arrowProps: any = { className: 'ml-auto', fontSize: 'small' };
    if (open) return <ArrowDropUp {...arrowProps} />;
    return <ArrowDropDown {...arrowProps} />;
  };

  const renderSelectText = () => {
    if (selectedText) return selectedText;
    return <span className="opacity-30">{placeholder}</span>;
  };

  const renderSelectContent = () => {
    const selectContent = (
      <div
        style={customStyles}
        className={clsx(classes.select, classes.variant[variant], {
          'pointer-events-none opacity-30': disabled,
        })}
      >
        <div
          style={customStyles}
          id="select-root"
          ref={setReferenceElement}
          className={classes.selection}
          onClick={() => setOpen(!open)}
        >
          <p style={customStyles} className="overflow-hidden text-ellipsis">
            {renderSelectText()}
          </p>
          {renderArrow()}
        </div>
        <input
          id={`Select-${formattedLabel}`}
          aria-labelledby={`label-${formattedLabel}`}
          aria-required={required}
          className={classes.input}
          onChange={onChange}
          tabIndex={-1}
          value={selectedValue}
          disabled={disabled}
          {...rest}
        />
        {open
          ? createPortal(
              <div
                ref={setMenuRef}
                className={clsx(classes.menu.root, classes.menu[variant])}
                onClick={handleSelection}
                onChange={onChange}
                style={{
                  ...styles.popper,
                  width:
                    referenceElement !== null
                      ? referenceElement.offsetWidth + 20
                      : 'fit-content',
                  minWidth: 'fit-content',
                }}
                {...attributes.popper}
              >
                {children}
              </div>,
              anchorEl ?? document.body
            )
          : null}
      </div>
    );

    if (label)
      return (
        <Label
          id={`label-${formattedLabel}`}
          htmlFor={`Select-${formattedLabel}`}
          className={classes.width[width]}
          aria-label={label}
          text={label}
          required={required}
        >
          {selectContent}
        </Label>
      );
    return selectContent;
  };

  return (
    <>
      <div
        style={customStyles}
        className={clsx(className, classes.container, classes.width[width])}
      >
        {renderSelectContent()}
        {renderHelperText()}
      </div>
    </>
  );
};

const useStyles = () => ({
  container: 'relative block p-1',
  helperText: {
    default: 'text-[0.75rem] mt-1',
    info: 'opacity-70',
    error: 'text-error',
  },
  hide: 'hidden',
  input:
    'absolute opacity-0 w-full left-0 bottom-0 pointer-events-none focus:border-primary active:border-primary z-[999999]',
  width: {
    full: 'w-full',
    unset: 'w-auto',
  },
  menu: {
    root: 'absolute z-[99999] block text-unset h-fit cursor-pointer rounded-b max-h-[200px] overflow-y-auto shadow-xl',
    default: 'bg-background-light dark:bg-background-dark',
    contained: '!-left-[5px] bg-gray-300 dark:bg-[#1B1E20]',
  },
  select: 'relative flex items-center cursor-pointer',
  selection:
    'inline-flex gap-2 cursor-pointer min-h-[10px] w-full whitespace-nowrap',
  variant: {
    default: 'border-b',
    contained:
      'bg-gray-300 dark:bg-[#1B1E20] rounded p-1 border border-transparent focus:border-primary active:border-primary',
  },
});

export { Select, SelectItem };
